// Dexie schema (SPEC §8). Every schema change appends a version here, with an
// upgrade function when data must move, and a fixture in tests/unit/db/
// that seeds the previous version (see migrations.test.ts).

import Dexie, { type EntityTable, type Transaction } from 'dexie';
import type {
  Channel, CompTrainingResult, Continuity, CrateCache, Cue, EqTrainingResult, HistoryEntry, Kit, Mark, Pattern, Project, RecordState, Sample, SampleBlob, Session, Settings,
} from '../data/types';

export const DB_NAME = 'lowend-lab';

export interface SchemaVersion {
  version: number;
  stores: Record<string, string | null>;
  upgrade?: (tx: Transaction) => Promise<void> | void;
}

export const SCHEMA_HISTORY: readonly SchemaVersion[] = [
  {
    version: 1,
    stores: {
      settings: '&key',
      continuity: '&key',
      samples: '&id, &hash, type, createdAt, favourite, deletedAt',
      blobs: '&key',
      kits: '&id, projectId, styleId, updatedAt, deletedAt',
      patterns: '&id, projectId, styleId, deletedAt',
      marks: '&id, videoId, projectId, createdAt, deletedAt',
      history: '++seq, videoId, at, styleId',
      channels: '++localId, id, handle, enabled',
      projects: '&id, styleId, updatedAt, archived, deletedAt',
    },
  },
  {
    // v0.3 Dig (SPEC F6): per-record state and cached crate shards. Booleans
    // aren't valid index keys, so bad and favourite are plain fields.
    version: 2,
    stores: {
      records: '&videoId, heardAt',
      crates: '&styleId',
    },
    // history rows with outcome 'bad' become bad records, and the latest
    // history time per video becomes heardAt.
    upgrade: async (tx) => {
      const history = await tx.table<HistoryEntry>('history').toArray();
      const records = new Map<string, RecordState>();
      for (const h of history) {
        const r = records.get(h.videoId) ?? { videoId: h.videoId };
        if (h.outcome === 'bad') r.bad = true;
        r.heardAt = Math.max(r.heardAt ?? 0, h.at);
        records.set(h.videoId, r);
      }
      await tx.table<RecordState>('records').bulkPut([...records.values()]);
    },
  },
  {
    // v0.4 Drums library (SPEC §8 "Indexes on samples"): *tags (multi-entry)
    // was missing, and favourite was indexed although IndexedDB can't index
    // booleans and SPEC §8 filters it in memory. No data transform: Dexie
    // rebuilds the indexes from the existing rows on open.
    version: 3,
    stores: {
      samples: '&id, &hash, type, *tags, createdAt, deletedAt',
    },
  },
  {
    // v0.6 Step C (F10 base): the session journal. A new table only: no data moves, so no upgrade function.
    // `Project.finishedAt` and `Continuity.checkoutSeen` are optional unindexed fields (DECISIONS 2026-10-08).
    version: 4,
    stores: {
      sessions: '&id, projectId, startedAt',
    },
  },
  {
    // v1.1 Chop preview (F6) and the trainers (F15, F16): new tables only, so no upgrade function. Cues are keyed
    // [videoId+pad] (one per pad); the two training tables stay empty until the trainers ship (one bump for all three Parts).
    version: 5,
    stores: {
      cues: '[videoId+pad], videoId',
      eqTraining: '&id, at',
      compTraining: '&id, at',
    },
  },
];

export const CURRENT_VERSION = SCHEMA_HISTORY.at(-1)?.version ?? 1;

export type LowEndDb = Dexie & {
  settings: EntityTable<Settings, 'key'>;
  continuity: EntityTable<Continuity, 'key'>;
  samples: EntityTable<Sample, 'id'>;
  blobs: EntityTable<SampleBlob, 'key'>;
  kits: EntityTable<Kit, 'id'>;
  patterns: EntityTable<Pattern, 'id'>;
  marks: EntityTable<Mark, 'id'>;
  history: EntityTable<HistoryEntry, 'seq'>;
  channels: EntityTable<Channel, 'localId'>;
  projects: EntityTable<Project, 'id'>;
  records: EntityTable<RecordState, 'videoId'>;
  crates: EntityTable<CrateCache, 'styleId'>;
  sessions: EntityTable<Session, 'id'>;
  cues: EntityTable<Cue, 'videoId'>;
  eqTraining: EntityTable<EqTrainingResult, 'id'>;
  compTraining: EntityTable<CompTrainingResult, 'id'>;
};

/** Opens the database with the schema history up to `upTo` (default: all). */
export function openDb(name = DB_NAME, upTo = CURRENT_VERSION): LowEndDb {
  const db = new Dexie(name) as LowEndDb;
  for (const v of SCHEMA_HISTORY) {
    if (v.version > upTo) break;
    const version = db.version(v.version).stores(v.stores);
    if (v.upgrade) version.upgrade(v.upgrade);
  }
  return db;
}

let instance: LowEndDb | undefined;

export function getDb(): LowEndDb {
  instance ??= openDb();
  return instance;
}

/** Tests only: drop the shared instance so the next getDb() reopens. */
export function resetDbForTests(): void {
  instance?.close();
  instance = undefined;
}
